import { EmailOutlined } from '@mui/icons-material'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  TextField,
} from '@mui/material'
import ImageBanner from 'components/image-banner'
import LogoTheme from 'components/logo-theme'
import PasswordValidation from 'components/password-validation'
import LoginGirlSVG from 'components/svg/login-girl'
import WrapperPermission from 'components/wrapper-permission'
import { useParams } from 'react-router-dom'
import { useStyles } from './styles'
import UseComponent from './use-component'

interface IRouteParams {
  token: string
}

const SignUpMultiple: React.FC = () => {
  const classes = useStyles()
  const { token } = useParams<IRouteParams>()

  const {
    form: {
      register,
      handleSubmit,
      formState: { errors },
      watch,
    },
    theme,
    isLoading,
    hasToken,
    isPasswordHidden,
    isHidePasswordRequirements,
    disabled,
    onSubmit,
    handleChange,
    setIsHidePasswordRequirements,
    setIsPasswordHidden,
    setDisabled,
  } = UseComponent({ token })

  return (
    <Box className={classes.container}>
      <Box className={classes.mainWrapper}>
        <WrapperPermission>
          <Box
            component='form'
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            data-testid='form'
            className={classes.formBox}
          >
            <LogoTheme />

            <span className={classes.formTitle}>
              Faça login em sua conta empresarial para autenticar e proteger seus dados privados com segurança.
            </span>

            <input
              hidden={true}
              {...register('token', { onChange: (e) => handleChange(e) })}
            />

            <TextField
              required
              id='email'
              label='E-mail'
              type='email'
              variant='outlined'
              placeholder='Digite seu e-mail'
              error={Boolean(errors.email)}
              helperText={errors?.email?.message}
              disabled={isLoading || hasToken}
              sx={{ mb: 3 }}
              {...register('email', { onChange: (e) => handleChange(e) })}
              InputProps={{
                startAdornment: (
                  <EmailOutlined
                    color={isLoading ? 'disabled' : 'inherit'}
                    sx={{ mr: 1.5 }}
                  />
                ),
              }}
            />

            <Box
              position='relative'
              width='100%'
            >
              <TextField
                required
                id='password'
                label='Senha'
                type={isPasswordHidden ? 'password' : 'text'}
                autoComplete='new-password'
                variant='outlined'
                placeholder='Escolha uma senha'
                error={Boolean(errors.password)}
                helperText={errors?.password?.message}
                onFocus={() => setIsHidePasswordRequirements(true)}
                onBlurCapture={() => setIsHidePasswordRequirements(false)}
                disabled={isLoading}
                {...register('password', { onChange: (e) => handleChange(e) })}
                InputProps={{
                  startAdornment: (
                    <LockOutlinedIcon
                      color={isLoading ? 'disabled' : 'inherit'}
                      sx={{ mr: 1.5 }}
                    />
                  ),
                  endAdornment: (
                    <IconButton
                      onClick={() => setIsPasswordHidden(!isPasswordHidden)}
                      disabled={isLoading}
                    >
                      {isPasswordHidden ? (
                        <VisibilityOffOutlinedIcon color={isLoading ? 'disabled' : 'inherit'} />
                      ) : (
                        <VisibilityOutlinedIcon />
                      )}
                    </IconButton>
                  ),
                  style: { paddingRight: 1 },
                }}
              />
              <PasswordValidation passwordString={watch('password') || ''} />
            </Box>

            <div className={classes.acceptArea}>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={(e) => setDisabled(!e.target.checked)}
                    disabled={isLoading}
                  />
                }
                label={
                  <div>
                    <span>Eu li e concordo </span>
                    <Link
                      download
                      href='/documents/Termose_Condicoes_Conducco-10_01_2024.pdf'
                    >
                      Termos de Uso
                    </Link>
                    <span> e a </span>
                    <Link
                      download
                      href='/documents/Politica_Privacidade.pdf'
                    >
                      Política de Privacidade
                    </Link>
                    <span> da Conducco</span>
                  </div>
                }
              />
            </div>

            <Button
              type='submit'
              fullWidth
              variant='contained'
              size='large'
              disabled={isLoading || disabled}
              sx={{
                mt: 2,
                mb: 1,
                '&.Mui-disabled': {
                  backgroundColor: '#8C9097 !important',
                  color: '#FFF !important',
                },
              }}
            >
              {isLoading ? (
                <CircularProgress
                  color='inherit'
                  size={26}
                />
              ) : (
                'CRIAR UMA CONTA'
              )}
            </Button>

            <Grid container>
              <Grid
                item
                xs={12}
                className={classes.signupLink}
              >
                Já tem uma conta?&nbsp;
                <Link
                  href='/'
                  variant='body1'
                  data-testid='signup-link'
                  underline='none'
                >
                  Conecte-se
                </Link>
              </Grid>
            </Grid>
          </Box>
        </WrapperPermission>

        <ImageBanner
          title='Conducco uma maneira eficiente e segura de gerenciar seus dados corporativos'
          banner={<LoginGirlSVG />}
        />
      </Box>
    </Box>
  )
}

export default SignUpMultiple
